/**
 * Axios Instance Configuration
 * Centralized HTTP client for API requests
 */
import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT } from '../../constants/apiConstants';
import { STORAGE_KEYS } from '../../constants/systemConfig';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get auth token from storage
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    // Add Authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp to prevent caching for GET requests
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle token refresh on 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
            refreshToken,
          });

          const { token } = response.data;
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Clear tokens and redirect to login
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);

        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;

      switch (status) {
        case 403:
          console.error('Access denied:', data.message);
          break;
        case 404:
          console.error('Resource not found:', data.message);
          break;
        case 422:
          console.error('Validation error:', data.errors);
          break;
        case 500:
          console.error('Server error:', data.message);
          break;
        default:
          console.error('API Error:', data.message);
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network error: No response received');
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Make API request
 * @param {string} method - HTTP method
 * @param {string} url - Endpoint URL
 * @param {Object} data - Request data
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    const response = await axiosInstance({
      method,
      url,
      data,
      ...config,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * GET request helper
 * @param {string} url - Endpoint URL
 * @param {Object} params - Query parameters
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const get = (url, params = {}, config = {}) =>
  apiRequest('get', url, null, { params, ...config });

/**
 * POST request helper
 * @param {string} url - Endpoint URL
 * @param {Object} data - Request data
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const post = (url, data = {}, config = {}) =>
  apiRequest('post', url, data, config);

/**
 * PUT request helper
 * @param {string} url - Endpoint URL
 * @param {Object} data - Request data
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const put = (url, data = {}, config = {}) =>
  apiRequest('put', url, data, config);

/**
 * PATCH request helper
 * @param {string} url - Endpoint URL
 * @param {Object} data - Request data
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const patch = (url, data = {}, config = {}) =>
  apiRequest('patch', url, data, config);

/**
 * DELETE request helper
 * @param {string} url - Endpoint URL
 * @param {Object} config - Additional config
 * @returns {Promise} API response
 */
export const del = (url, config = {}) => apiRequest('delete', url, null, config);

export default axiosInstance;
